import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import styled from 'styled-components';
import useDetectScrollPosition from '../../hooks/useDetectScrollPosition';
import {
  MessageListBackground,
  MessageHeader,
  Loading,
} from "@minchat/react-chat-ui";
import { useChat } from "../../contexts/ChatProvider";
import CarouselMessage from "./CarouselMessage";
import Message from "./Message";
import Avatar from "./Avatar";
import TypingIndicator from "../typing-indicator";

const Container = styled.div`
    height: 100%;
    position: relative;
    max-height: 100vh;
    overflow-y: hidden;
    padding-left: 0px;
    padding-right: 12px;
    padding-bottom: 2px !important;
`;

const InnerContainer = styled.div`
    height: 100%;
    padding-bottom: 2px !important;
`;

const ScrollContainer = styled.div`
    overflow-y: auto;
    position: relative;
    height: 100%;
    width: 100%;
    max-height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    scrollbar-width: none;
    // -ms-overflow-style: none;
    // ::-webkit-scrollbar {
    //     width: 0;
    //     height: 0;
    // }
    ::-webkit-scrollbar {
        background-color: #aaaaaa;
        height: 4px;
      }
      ::-webkit-scrollbar-track {
        background: #f1f1f1;
      }
      ::-webkit-scrollbar-thumb {
        background-color: #888;
        border-radius: 1px;
      }
      ::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
`;

const Buffer = styled.div`
    height: 6px;
    width: 100%;
    position: relative;
`;


const RetryLink = styled.div`
    color: #007bff; /* Set the text color */
    text-decoration: underline; /* Add underline decoration */
    cursor: pointer; /* Show pointer cursor on hover */
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
    "Segoe UI Symbol", "Noto Color Emoji";
    font-size: 14px;
`;

const RetryMessageContainer = styled.div`
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
    "Segoe UI Symbol", "Noto Color Emoji";
    font-size: 14px;
    margin: 2px;
`;

const RetryContainer = styled.div`
  width: 100%;
  height: 100%;
  align-items: center;
  z-index: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  position: relative;
`;

const MessageList = ({
  loading = false,
  typingIndicatorContent,
  customTypingIndicatorComponent,
  onSendMessage
}) => {
  const {
    showTypingIndicator,
    theme,
    messages,
    fetchConversations,
    setLoading,
    loadConversation,
    error,
    setError,
  } = useChat();
  const containerRef = useRef(null);
  const bottomBufferRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [ scrollDownIndicator, setScrollDownIndicator ] = useState(false);

  useEffect(() => {
    setTimeout(() => { scrollToBottom();}, 0);
  }, [scrollDownIndicator]);

  // Initial call
  useEffect(() => {
    if (loadConversation) {
      setLoading(true);
      fetchConversations()
          .then(() => setLoading(false))
          .catch(() => setLoading(false));
    }
  }, [loadConversation]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container && container.scrollTop === 0) {
      const currentScrollPosition = container.scrollHeight - container.scrollTop;
      setLoadingMore(true);
      fetchConversations()
          .then(() => {
            setLoadingMore(false);
            container.scrollTop = container.scrollHeight - currentScrollPosition;
          })
          .catch(() => setLoadingMore(false));
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToBottom = () => {
    if (bottomBufferRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollPoint = bottomBufferRef.current;
      const parentRect = container.getBoundingClientRect();
      const childRect = scrollPoint.getBoundingClientRect();
      const scrollOffset = childRect.top + container.scrollTop - parentRect.top;

      if (container.scrollBy) {
        container.scrollBy({ top: scrollOffset, behavior: "auto" });
      } else {
        container.scrollTop = scrollOffset;
      }
    }
  };

  return (
    <Container ref={containerRef}>
      <MessageListBackground roundedCorners={false} />
      <InnerContainer>
        {loading ? (
          <LoadingContainer>
            {<Loading themeColor={theme} />}
          </LoadingContainer>
        ) : error ?
          <RetryContainer>
            <RetryMessageContainer>
              Failed to load the conversation
            </RetryMessageContainer>
            <RetryLink onClick={() => {
              setLoading(true);
              setError('');
              fetchConversations()
                .then(() => setLoading(false))
                .catch(() => setLoading(false));
            }}>
              Try again
            </RetryLink>
          </RetryContainer>
          : (
            <ScrollContainer
              onScroll={handleScroll}
              ref={scrollContainerRef}
            >
              {messages && messages.map((message, index) => {
                // Determine the type of message to render
                let lastClusterMessage, firstClusterMessage, last, single;


                //if it is the first message in the messages array then show the header
                if (index === 0) {
                  firstClusterMessage = true
                }
                //if the previous message from a different  then show the header
                if (index > 0 && messages[index - 1].user !== message.user) {
                  firstClusterMessage = true
                }
                //if it is the last message in the messages array then show the avatar and is the last incoming
                if (index === messages.length - 1) {
                  lastClusterMessage = true;
                  last = true
                }
                //if the next message from a different user then show the avatar and is last message incoming
                if (index < messages.length - 1 && messages[index + 1].user !== message.user) {
                  lastClusterMessage = true;
                  last = true
                }
                //if the next message and the previous message are not from the same message.user then single incoming is true
                if (index < messages.length - 1 && index > 0 && messages[index + 1].user !== message.user && messages[index - 1].user !== message.user) {
                  single = true
                }
                //if it is the first message in the messages array and the next message is from a different user then single incoming is true
                if (index === 0 && index < messages.length - 1 && messages[index + 1].user !== message.user) {
                  single = true
                }
                //if it is the last message in the messages array and the previous message is from a different user then single incoming is true
                if (index === messages.length - 1 && index > 0 && messages[index - 1].user !== message.user) {
                  single = true
                }
                //if the messages array contains only 1 message then single incoming is true
                if (messages.length === 1) {
                  single = true
                }

                if (message.type === 'outgoing') {
                  return (
                    <div key={message._id} style={{ marginTop: '16px' }}>
                      <Message
                        type={message.type}
                        text={message.text}
                        seen={message.seen}
                        created_at={message.createdAt}
                        showAvatar={true}
                        last={single ? false : last}
                        single={single}
                        scrollToBottom={scrollToBottom}
                      // Add other message properties as needed
                      />
                    </div>
                  )
                } else if (message.type === 'carousel') {
                  return (
                    <CarouselMessage
                      type='incoming'
                      key={message._id}
                      _id={message._id}
                      user={message.user}
                      recommendations={message.recommendations}
                      media={{ urls: ['https://picsum.photos/id/1/100', 'https://picsum.photos/id/2/100', 'https://picsum.photos/id/3/100'] }}
                      seen={message.seen}
                      created_at={message.createdAt}
                      showAvatar={true}
                      text={''}
                      windowWidth={containerRef?.current?.clientWidth}
                    />
                  )
                } else {
                  return (
                    <div key={message._id}>
                      <Message
                        type={message.type}
                        text={message.text}
                        question_data={message.question_data}
                        question_data_answer={message.question_data_answer}
                        handleCheckboxVisibility={(value) => setScrollDownIndicator(value)}
                        onSendMessage={onSendMessage}
                        seen={message.seen}
                        created_at={message.createdAt}
                        showAvatar={true}
                        avatar={message.avatar}
                        showHeader={firstClusterMessage}
                        clusterFirstMessage={firstClusterMessage}
                        clusterLastMessage={lastClusterMessage}
                        isLastMessage={index === messages.length - 1}
                        last={single ? false : last}
                        single={single}
                        style={{
                          marginRight: '10px',
                          marginTop: '16px !important',
                          marginBottom: '16px !important'
                        }}
                      // Add other message properties as needed
                      />
                    </div>
                  );
                }
              })}

              {showTypingIndicator && (
                <div style={{ display: 'flex', justifyContent: 'start' }}>
                  {customTypingIndicatorComponent ?
                    customTypingIndicatorComponent
                    :
                    <TypingIndicator
                      content={typingIndicatorContent}
                      themeColor={theme} />}
                </div>
              )}
              <Buffer ref={bottomBufferRef} />
            </ScrollContainer>
          )}
      </InnerContainer>
    </Container>
  );
};

export default MessageList;
